import axios from 'axios'
import type { AxiosResponse, InternalAxiosRequestConfig } from 'axios'
import { camelizeKeys, decamelizeKeys } from 'humps'
import get from 'lodash/get'

import loginStorage from 'utils/loginStorage'

/**
 * loginStorage から認証ヘッダーを config にセットする
 */
export const setAuthHeaders = (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
  const sessionData = loginStorage.getAuthData()
  config.headers.set('uid', get(sessionData, 'uid', ''))
  config.headers.set('client', get(sessionData, 'client', ''))
  config.headers.set('access-token', get(sessionData, 'accessToken', ''))
  return config
}

/**
 * リクエストの params / data を snake_case に変換する。
 *
 * `config.skipDataDecamelize === true` のときは data の変換をスキップする。
 * 呼び出し側が既に snake_case 化済みのボディ（一部に camelCase を保持したいネスト等）を
 * 送りたいケースのためのエスケープハッチ。
 */
export const decamelizeRequestInterceptor = (
  config: InternalAxiosRequestConfig & { skipDataDecamelize?: boolean }
): InternalAxiosRequestConfig => {
  if (config.params) {
    config.params = decamelizeKeys(config.params)
  }
  if (config.data && !config.skipDataDecamelize) {
    config.data = decamelizeKeys(config.data)
  }
  return config
}

/**
 * レスポンスの data を camelCase に変換する（Blob は除外）
 */
export const camelizeResponseInterceptor = (response: AxiosResponse): AxiosResponse => {
  if (response.data instanceof Blob) {
    return response
  }
  response.data = camelizeKeys(response.data)
  return response
}

/**
 * エラーレスポンスの data を camelCase に変換して reject し直す（Blob は除外）。
 *
 * 成功時の [camelizeResponseInterceptor] と対で使い、呼び出し側が成功・失敗で
 * キーの命名規則を使い分けずに済むようにする。`response` を持たないエラー
 * （ネットワークエラー / リクエスト設定エラー）はそのまま透過する。
 */
export const camelizeErrorInterceptor = (error: unknown): Promise<never> => {
  const response = axios.isAxiosError(error) ? error.response : undefined
  if (response && !(response.data instanceof Blob)) {
    response.data = camelizeKeys(response.data)
  }
  return Promise.reject(error)
}
